/**
 * Configuration constants for sticky ATB functionality.
 */
const TIMING = {
  INIT_DELAY: 100
};

/**
 * DOM selectors for sticky add-to-bag functionality.
 * @type {Object<string, string>}
 */
const selectors = {
  atb: '.product-form__submit:not(.quick-add__submit)',
  stickyAtb: '.sticky-atb',
  mainForm: 'form[id*="product-form-template"]:not([id*="quick-add"]), product-form form:not([id*="quick-add"])',
  stickyForm: '.sticky-atb form'
};

document.addEventListener('DOMContentLoaded', () => {

  /**
   * DOM node selectors.
   * @type {Object<string, HTMLElement|null>}
   */
  const nodeSelectors = {
    atb: document.querySelector(selectors.atb),
    stickyAtb: document.querySelector(selectors.stickyAtb),
    mainForm: document.querySelector(selectors.mainForm),
    stickyForm: document.querySelector(selectors.stickyForm)
  };

  /**
   * Tracks whether the main CTA has been visible to prevent premature sticky bar appearance.
   * @type {boolean}
   */
  let hasBeenVisible = false;

  /**
   * Handles intersection observer callback for sticky ATB visibility.
   * Shows sticky bar only after main CTA has been visible and is now out of view.
   * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries
   */
  const handleIntersection = (entries) => {
    const entry = entries[0];

    if (entry.isIntersecting && !hasBeenVisible) {
      hasBeenVisible = true;
    }

    const shouldHide = entry.isIntersecting || !hasBeenVisible;
    nodeSelectors.stickyAtb.classList.toggle('is-hidden', shouldHide);
  };

  /**
   * Intersection Observer for desktop sticky ATB functionality.
   * @type {IntersectionObserver}
   */
  const observer = new IntersectionObserver(handleIntersection, {
    rootMargin: '0px 0px -50px 0px',
    threshold: [0]
  });

  /**
   * Syncs Recharge subscription properties from main form to sticky form.
   * This ensures subscription selections are maintained when using sticky ATB.
   */
  const syncRechargeProperties = () => {
    if (!nodeSelectors.mainForm || !nodeSelectors.stickyForm) return;

    const existingRechargeInputs = nodeSelectors.stickyForm.querySelectorAll('[name^="properties"], [name="selling_plan"], [name^="rc_"], input[data-recharge]');
    existingRechargeInputs.forEach(input => {
      if (input && input.parentNode === nodeSelectors.stickyForm) {
        input.remove();
      }
    });

    const rechargeInputs = nodeSelectors.mainForm.querySelectorAll('input[name="selling_plan"], input[name^="properties"], input[name^="rc_"], input[data-recharge]');

    rechargeInputs.forEach(input => {
      if (input.disabled) return;

      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;

      const clonedInput = input.cloneNode(true);

      if (input.type !== 'hidden') {
        clonedInput.type = 'hidden';
      }

      nodeSelectors.stickyForm.appendChild(clonedInput);
    });

    const rechargeSelects = nodeSelectors.mainForm.querySelectorAll('select[name="selling_plan"], select[name^="properties"]');
    rechargeSelects.forEach(select => {
      if (select.value) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = select.name;
        hiddenInput.value = select.value;
        nodeSelectors.stickyForm.appendChild(hiddenInput);
      }
    });
  };

  /**
   * Observes changes to the main form to sync with sticky form.
   * Uses MutationObserver to watch for Recharge widget changes and form events.
   */
  const observeFormChanges = () => {
    if (!nodeSelectors.mainForm) return;

    const observer = new MutationObserver(() => {
      syncRechargeProperties();
    });

    observer.observe(nodeSelectors.mainForm, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'checked']
    });

    nodeSelectors.mainForm.addEventListener('change', syncRechargeProperties);
  };

  /**
   * Initializes the sticky ATB observer and Recharge sync functionality.
   * Sets up intersection observer and form synchronization with appropriate delays.
   */
  const initializeStickyATB = () => {
    if (nodeSelectors.atb && nodeSelectors.stickyAtb) {
      observer.observe(nodeSelectors.atb);
      syncRechargeProperties();
      observeFormChanges();
    }
  };

  setTimeout(initializeStickyATB, TIMING.INIT_DELAY);

  /**
   * Handles sticky form submission to ensure subscription data is synced.
   * Performs final sync before form submission to capture latest subscription state.
   */
  if (nodeSelectors.stickyForm) {
    nodeSelectors.stickyForm.addEventListener('submit', () => {
      syncRechargeProperties();
    });
  }

  /**
   * Handles variant change events to update sticky ATB form fields.
   * Updates variant ID and price display when product variant is changed.
   * @param {Object} event - Variant change event containing variant data
   */
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const { id: variantId, price: variantPrice } = event?.data?.variant || {};
    
    const idField = nodeSelectors.stickyAtb.querySelector('[name="id"]');
    const priceField = nodeSelectors.stickyAtb.querySelector('.price-item--regular');

    if (idField && variantId) {
      idField.value = variantId;
    }

    if (priceField && variantPrice) {
      priceField.innerHTML = Shopify.formatMoney(variantPrice);
    }
  });
});
